import { getInputComponent, getOutputComponent, getPreviewComponent } from '@components/fields';
import { DobField } from '@components/forms/DobField';
import { FileField } from '@components/forms/FileField';
import { FullNameField } from '@components/forms/FullNameField';
import { InputWithSuggestion } from '@components/forms/InputWithSuggestion';
import { RadioCardGroup } from '@components/forms/RadioCardGroup';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@components/ui/accordion';
import { Button } from '@components/ui/button';
import { DatePicker } from '@components/ui/date-picker';
import { DispositionFieldIcon, SectionScoreBadge } from '@components/ui/disposition-status';
import { EditableField, shouldShowExtractedValue } from '@components/ui/editable-field';
import { EditableSummary } from '@components/ui/editable-summary';
import { FieldEscalateControl } from '@components/ui/field-escalation';
import { HeardSeekButton } from '@components/ui/heard-seek-button';
import { Input } from '@components/ui/input';
import { MultiSelect } from '@components/ui/multi-select';
import { NestedFieldAccordion } from '@components/ui/nested-field-accordion';
import { ReasoningTooltip, ReviewButton } from '@components/ui/review-button';
import { SearchableSelect } from '@components/ui/searchable-select';
import {
  areAllAccordionItemsExpanded,
  createAllAccordionIndices,
  toggleAllAccordionItems,
} from '@utils/accordion';
import { computeAutoCalculatedValue } from '@utils/autoCalculate';
import { separateMetadataGroup } from '@utils/cases';
import { UNKNOWN_DATE_VALUE } from '@utils/date';
import { hasOnlyHiddenFields } from '@utils/fieldVisibility';
import { formatComplexValue } from '@utils/formatValue';
import { createEmptyItem, separateFieldTypes, setNestedValue } from '@utils/nestedAccordion';
import { applyRequiredFieldsToGroups } from '@utils/schemaValidation';
import { format } from 'date-fns';
import { ChevronDown, ChevronsDownUp, ChevronUp, Edit3, Plus } from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactPhoneInput from 'react-phone-input-2';

import type { FieldEvidence, FieldMetadata, HeardSeek } from '@/components/fields/types';
import type { GetFieldSuggestionProps } from '@/hooks/useCaseInputStorage';
import type { EditableFieldState } from '@/types/editableField';
import type { FormField } from '@/types/formSchema';
import type { DispositionFieldStatus } from '@/utils/dispositionCompleteness';
import {
  buildFieldDispositionMap,
  computeFieldScore,
  computeSectionScores,
} from '@/utils/dispositionCompleteness';
import { getCallSourceId } from '@/utils/evidence';

export interface FieldGroup {
  label: string;
  fields: FormField[];
  summary?: string; // Optional summary text to display as subtitle
}

interface FieldGroupAccordionProps {
  groups: FieldGroup[];
  data: any;
  extractedData?: any; // AI-extracted data to display alongside main data
  editableState?: EditableFieldState | null;
  emptyStateMessage?: {
    title: string;
    subtitle: string;
  };
  className?: {
    accordion?: string;
    accordionContent?: string;
    fieldContainer?: string;
    fieldWrapper?: string;
    fieldSeparator?: string;
  };
  fieldClassName?: {
    container?: string;
    label?: string;
    value?: string;
    editButton?: string;
    nonEditingLayout?: string;
    labelInline?: boolean;
  };
  isEditable?: boolean;
  /** Which data section this accordion renders — controls component registry selection */
  section?: 'input' | 'output';
  onDataChange?: (updatedData: any, fieldPath?: string) => void;
  onFieldBlur?: (fieldPath: string) => void;
  showCollapseAll?: boolean; // Show collapse/expand all button
  defaultExpanded?: boolean; // Default expansion state
  externalExpandedItems?: string[]; // External control of expanded items
  onExpandedItemsChange?: (items: string[]) => void; // Callback for external control
  // Summary editing props
  canEditSummaries?: boolean; // Whether summaries can be edited
  editingSummaries?: Set<string>; // Set of section labels currently being edited
  onSummaryEdit?: (sectionLabel: string) => void; // Callback when edit button clicked
  onSummarySave?: (sectionLabel: string, newSummary: string) => void; // Callback to save
  onSummaryCancel?: (sectionLabel: string) => void; // Callback to cancel editing
  isLoadingSummary?: boolean; // Loading state for summary operations
  // Group-level edit button
  onGroupEditClick?: (groupIndex: number) => void; // Callback when group edit button is clicked
  fieldError?: Record<string, string>;
  isCaseReview?: boolean;
  /** Consolidated per-field suggestion props from useCaseInputStorage */
  getFieldSuggestionProps?: GetFieldSuggestionProps;
  /** Case ID for File-type fields, so uploads target the existing case. */
  caseRecordId?: string;
  /** Workflow type (e.g., 'medbv', 'pbm', 'unifiedmedbv') */
  workflowType?: string;
  /** Disposition fields for computing per-section completeness scores */
  dispositionFields?: { required_fields?: string[]; missing_fields?: string[] };
  /** Whether the case is in human audit state */
  isHumanAudit?: boolean;
  /** Callback when user accepts an extracted value */
  onAcceptExtracted?: (fieldPath: string, value: any, fieldLabel: string) => void;
  /** Metadata about extracted fields (per-field source_id + evidence) */
  extractedMetadata?: FieldMetadata;
  /** Metadata about output_data fields (evidence for the main value line) */
  outputMetadata?: FieldMetadata;
  /** Callback to navigate to a specific call in the Review tab */
  onNavigateToCall?: (callId: string, evidenceTimeStart?: string, evidenceTimeEnd?: string) => void;
  /** Callback to navigate to the AI Audit tab when an audit-sourced evidence is clicked */
  onNavigateToAudit?: (auditId: string) => void;
  /** The ID of the call currently being viewed — used to filter sparkles to current-call extractions only */
  currentCallId?: string;
}

// Helper function to safely access nested data
const getNestedValue = (
  obj: any,
  path: string,
  defaultValue: string = '--',
  isEditing: boolean = false,
) => {
  const keys = path.split('.');
  let value = obj;

  for (const key of keys) {
    value = value?.[key];
    if (value === undefined || value === null) {
      return isEditing ? '' : defaultValue;
    }
  }

  // Return the value directly if it's not null/undefined
  // This handles false, 0, empty string, etc. correctly
  if (value !== undefined && value !== null) {
    return value;
  }

  return isEditing ? '' : defaultValue;
};

// Component for displaying summary with show more/less functionality
const SummaryDisplay: React.FC<{
  summary: string;
  isExpanded: boolean;
  onTruncationDetected: (isTruncated: boolean) => void;
}> = ({ summary, isExpanded, onTruncationDetected }) => {
  const summaryRef = useRef<HTMLDivElement>(null);

  // Detect if text is truncated
  useEffect(() => {
    const element = summaryRef.current;
    if (element && !isExpanded) {
      // Check if content is taller than 2 lines
      const isTruncated = element.scrollHeight > element.clientHeight;
      onTruncationDetected(isTruncated);
    }
  }, [summary, isExpanded, onTruncationDetected]);

  return (
    <div className="mt-1">
      <div
        ref={summaryRef}
        className={`text-xs font-normal text-muted-foreground ${isExpanded ? '' : 'line-clamp-2'}`}
      >
        {summary}
      </div>
    </div>
  );
};

// Interface for GroupFieldsRenderer props
interface GroupFieldsRendererProps {
  fields: FormField[];
  /** Label of the group these fields belong to — used to drop a nested field's
   *  redundant heading when the schema gives it the same name as its group. */
  groupLabel?: string;
  data: any;
  extractedData?: any; // AI-extracted data to display alongside main data
  isEditable: boolean;
  /** Which data section this renderer belongs to — controls component registry selection */
  section?: 'input' | 'output';
  /** Workflow type — scopes component registry lookups so a workflow only gets
   * the custom array components registered for it (BI-1442) */
  workflowType?: string;
  fieldError?: Record<string, string>;
  fieldClassName: {
    container?: string;
    label?: string;
    value?: string;
    editButton?: string;
    nonEditingLayout?: string;
    labelInline?: boolean;
  };
  editableState: EditableFieldState | null;
  classes: {
    fieldContainer: string;
    fieldWrapper: string;
    fieldSeparator: string;
  };
  handleDataChange: (fieldPath: string, value: any) => void;
  onFieldBlur?: (fieldPath: string) => void;
  /** Consolidated per-field suggestion props from useCaseInputStorage */
  getFieldSuggestionProps?: GetFieldSuggestionProps;
  /** Per-field disposition status map */
  dispositionMap?: Map<string, DispositionFieldStatus>;
  /** Raw disposition fields, threaded down to array output components (DrugOutputField, etc.) */
  dispositionFields?: { required_fields?: string[]; missing_fields?: string[] };
  /** Whether the case is in human audit state */
  isHumanAudit?: boolean;
  /** Callback when user accepts an extracted value */
  onAcceptExtracted?: (fieldPath: string, value: any, fieldLabel: string) => void;
  /** Metadata about extracted fields (per-field source_id + evidence) */
  extractedMetadata?: FieldMetadata;
  /** Metadata about output_data fields (evidence for the main value line) */
  outputMetadata?: FieldMetadata;
  /** Callback to navigate to a specific call in the Review tab */
  onNavigateToCall?: (callId: string, evidenceTimeStart?: string, evidenceTimeEnd?: string) => void;
  /** Callback to navigate to the AI Audit tab when an audit-sourced evidence is clicked */
  onNavigateToAudit?: (auditId: string) => void;
  /** The ID of the call currently being viewed — used to filter sparkles to current-call extractions only */
  currentCallId?: string;
  /** Case ID for File-type fields, so uploads target the existing case. */
  caseRecordId?: string;
}

/**
 * Renders group fields, separating scalar fields from nested (array/object) fields.
 * Scalar fields are rendered inline, while nested fields get their own accordion sections.
 */
const GroupFieldsRenderer: React.FC<GroupFieldsRendererProps> = ({
  fields,
  groupLabel,
  data,
  extractedData,
  isEditable,
  section,
  workflowType,
  fieldError,
  fieldClassName,
  editableState,
  classes,
  handleDataChange,
  onFieldBlur,
  getFieldSuggestionProps,
  dispositionMap,
  dispositionFields,
  isHumanAudit,
  onAcceptExtracted,
  extractedMetadata,
  outputMetadata,
  onNavigateToCall,
  onNavigateToAudit,
  currentCallId,
  caseRecordId,
}) => {
  // Separate scalar and nested fields
  const { scalarFields, nestedFields } = useMemo(() => separateFieldTypes(fields), [fields]);
  const hasDisposition = dispositionMap && dispositionMap.size > 0;

  // Track expanded state for nested field accordions
  const [expandedNestedFields, setExpandedNestedFields] = useState<string[]>([]);
  // Track expanded items within nested accordions (for auto-expand on add)
  const [nestedExpandedItems, setNestedExpandedItems] = useState<string[]>([]);

  return (
    <div className={classes.fieldContainer}>
      {/* Render scalar fields */}
      {scalarFields.map((field, index) => {
        const value = getNestedValue(data, field.id, '--', isEditable);
        const isLastScalarField = index === scalarFields.length - 1 && nestedFields.length === 0;
        const error = fieldError?.[field.id];

        const isMirrorDisabled =
          !!field.mirrorFieldId &&
          !!field.mirrorWhenField &&
          !!field.mirrorWhenValue &&
          String(data[field.mirrorWhenField]) === field.mirrorWhenValue;

        const fieldContent = isEditable ? (
          <ScalarFieldEditable
            field={{ ...field, disabled: field.disabled || isMirrorDisabled }}
            value={value}
            error={error}
            fieldClassName={fieldClassName}
            handleDataChange={handleDataChange}
            onFieldBlur={onFieldBlur}
            getFieldSuggestionProps={getFieldSuggestionProps}
            caseRecordId={caseRecordId}
            outputHeardSeek={outputMetadata?.fields?.[field.id]?.heardSeek}
            onNavigateToCall={onNavigateToCall}
            extractedValue={extractedData ? (extractedData[field.id] ?? null) : undefined}
            extractedEvidence={extractedMetadata?.fields?.[field.id]?.evidence}
            extractedSourceCallId={getCallSourceId(extractedMetadata?.fields?.[field.id])}
            outputEvidence={outputMetadata?.fields?.[field.id]?.evidence}
            onNavigateToAudit={onNavigateToAudit}
            currentCallId={currentCallId}
          />
        ) : (
          <EditableField
            label={field.label}
            fieldPath={field.id}
            value={value}
            extractedValue={extractedData ? (extractedData[field.id] ?? null) : undefined}
            field={field}
            editableState={editableState}
            className={fieldClassName}
            hasError={false}
            isHumanAudit={isHumanAudit}
            onAcceptExtracted={onAcceptExtracted}
            sourceCallId={getCallSourceId(extractedMetadata?.fields?.[field.id])}
            evidence={extractedMetadata?.fields?.[field.id]?.evidence}
            outputEvidence={outputMetadata?.fields?.[field.id]?.evidence}
            outputHeardSeek={outputMetadata?.fields?.[field.id]?.heardSeek}
            onNavigateToCall={onNavigateToCall}
            onNavigateToAudit={onNavigateToAudit}
            currentCallId={currentCallId}
            caseRecordId={caseRecordId}
          />
        );

        return (
          <div
            key={field.id}
            data-field-id={field.id}
            className={`${classes.fieldWrapper} ${!isLastScalarField ? classes.fieldSeparator : ''}`}
          >
            {/* The Escalate icon sits in the field's own row (and the field narrows by its
                width) — only under a FieldEscalationProvider, i.e. the Human Audit
                in-progress board in edit mode; elsewhere this passes the field through. */}
            <FieldEscalateControl fieldPath={field.id} fieldLabel={field.label}>
              {hasDisposition ? (
                <div className="flex items-start gap-2">
                  <div className="pt-2.5">
                    <DispositionFieldIcon status={dispositionMap.get(field.id)} />
                  </div>
                  <div className="flex-1 min-w-0">{fieldContent}</div>
                </div>
              ) : (
                fieldContent
              )}
            </FieldEscalateControl>
          </div>
        );
      })}

      {/* Render nested (array/object) fields as sub-accordions */}
      {nestedFields.length > 0 && (
        <div className={scalarFields.length > 0 ? 'mt-3 pt-3 border-t' : ''}>
          <Accordion
            type="multiple"
            value={expandedNestedFields}
            onValueChange={setExpandedNestedFields}
            className="space-y-2"
          >
            {nestedFields.map(field => {
              const fieldData = data?.[field.id];

              const handleAddArrayItem = (e: React.MouseEvent) => {
                e.stopPropagation();
                if (field.type !== 'array') return;

                const currentItems = Array.isArray(fieldData) ? fieldData : [];
                const newItem = createEmptyItem(field);
                handleDataChange(field.id, [...currentItems, newItem]);

                // Auto-expand the field accordion if not already expanded
                if (!expandedNestedFields.includes(field.id)) {
                  setExpandedNestedFields([...expandedNestedFields, field.id]);
                }

                // Auto-expand the newly added item inside NestedFieldAccordion
                const newItemPath = `${field.id}.${currentItems.length}`;
                setNestedExpandedItems(prev => [...prev, newItemPath]);
              };

              const showAddButton =
                isEditable &&
                field.type === 'array' &&
                !(section === 'input'
                  ? getInputComponent(field.id, workflowType)
                  : section === 'output'
                    ? getOutputComponent(field.id, workflowType)
                    : getInputComponent(field.id, workflowType));

              // Registry-aware body for this nested field, shared by both branches below
              // (with and without the field's own accordion heading).
              const nestedContent = (() => {
                // Section-aware component selection
                if (section === 'input') {
                  const InputComp = getInputComponent(field.id, workflowType);
                  if (InputComp) {
                    return React.createElement(InputComp, {
                      data: fieldData || [],
                      onChange: (newData: unknown[]) => handleDataChange(field.id, newData),
                      errors: fieldError,
                      disabled: !isEditable,
                    });
                  }
                } else if (section === 'output') {
                  const OutputComp = getOutputComponent(field.id, workflowType);
                  if (OutputComp && !isEditable) {
                    return React.createElement(OutputComp, {
                      data: fieldData || [],
                      outputMetadata,
                      extractedMetadata,
                      extractedData: extractedData?.[field.id],
                      onNavigateToCall,
                      onNavigateToAudit,
                      fieldId: field.id,
                      isHumanAudit,
                      onAcceptExtracted,
                      dispositionFields,
                      currentCallId,
                    });
                  }
                  // Output section in edit mode: output components are display-only,
                  // fall through to NestedFieldAccordion for editing
                }

                // Legacy behavior when section not specified
                if (section === undefined) {
                  if (isEditable && getInputComponent(field.id, workflowType)) {
                    return React.createElement(getInputComponent(field.id, workflowType)!, {
                      data: fieldData || [],
                      onChange: (newData: unknown[]) => handleDataChange(field.id, newData),
                      errors: fieldError,
                      disabled: false,
                    });
                  }
                  if (!isEditable && getOutputComponent(field.id, workflowType)) {
                    return React.createElement(getOutputComponent(field.id, workflowType)!, {
                      data: fieldData || [],
                      outputMetadata,
                      extractedMetadata,
                      extractedData: extractedData?.[field.id],
                      onNavigateToCall,
                      onNavigateToAudit,
                      fieldId: field.id,
                      isHumanAudit,
                      onAcceptExtracted,
                      dispositionFields,
                      currentCallId,
                    });
                  }
                }

                // Default: generic nested field accordion
                return (
                  <NestedFieldAccordion
                    field={field}
                    data={fieldData}
                    path={field.id}
                    depth={1}
                    isEditable={isEditable}
                    onDataChange={handleDataChange}
                    onFieldBlur={onFieldBlur}
                    fieldError={fieldError}
                    expandedItems={nestedExpandedItems}
                    onExpandedItemsChange={setNestedExpandedItems}
                    dispositionFields={dispositionFields}
                    getFieldSuggestionProps={getFieldSuggestionProps}
                    outputMetaNode={
                      outputMetadata?.fields?.[field.id] as Record<string, unknown> | undefined
                    }
                    // Same AI-extraction context flat fields get below, scoped to
                    // this entity's subtree — without it the leaves under drug /
                    // procedure rendered no suggestion and no reasoning at all.
                    extractedDataNode={
                      extractedData?.[field.id] as Record<string, unknown> | undefined
                    }
                    extractedMetaNode={
                      extractedMetadata?.fields?.[field.id] as Record<string, unknown> | undefined
                    }
                    onAcceptExtracted={onAcceptExtracted}
                    isHumanAudit={isHumanAudit}
                    currentCallId={currentCallId}
                    onNavigateToCall={onNavigateToCall}
                    onNavigateToAudit={onNavigateToAudit}
                    className={{
                      container: fieldClassName.container,
                      label: fieldClassName.label,
                      value: fieldClassName.value,
                    }}
                  />
                );
              })();

              // A schema that names a nested field after its own group (medbv's
              // `drug` / `procedure` carry group AND label "Drug Details" /
              // "Procedure Details") rendered the heading twice — group, then an
              // identical field accordion, then the J-code items. When the group
              // holds nothing but that one field, drop the inner accordion and let
              // the group header stand in for it. Nothing is lost: the group header
              // already carries the score badge, and ArrayFieldRenderer renders its
              // own Add control.
              const dropsRedundantHeading =
                nestedFields.length === 1 &&
                scalarFields.length === 0 &&
                !!groupLabel &&
                field.label.trim().toLowerCase() === groupLabel.trim().toLowerCase();

              if (dropsRedundantHeading) {
                return (
                  <div key={field.id} data-accordion-path={field.id} className="pt-1">
                    {nestedContent}
                  </div>
                );
              }

              return (
                <AccordionItem
                  key={field.id}
                  value={field.id}
                  data-accordion-path={field.id}
                  className="border rounded-md"
                >
                  <AccordionTrigger
                    className="px-3 py-2 text-sm font-medium hover:bg-muted/50 rounded-md data-[state=open]:rounded-b-none"
                    action={
                      showAddButton ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={handleAddArrayItem}
                          className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground mr-2"
                        >
                          <Plus className="w-3 h-3 mr-1" />
                          Add
                        </Button>
                      ) : null
                    }
                  >
                    <div className="flex items-center gap-2 flex-1">
                      <span>
                        {field.label}
                        {field.validation?.required && (
                          <span className="text-destructive ml-1">*</span>
                        )}
                      </span>
                      {field.type === 'array' &&
                        Array.isArray(fieldData) &&
                        fieldData.length > 0 &&
                        (() => {
                          const PreviewComponent = getPreviewComponent(field.id, workflowType);
                          return PreviewComponent ? (
                            <PreviewComponent data={fieldData} maxItems={2} />
                          ) : (
                            <span className="text-xs text-muted-foreground">
                              ({formatComplexValue(fieldData)})
                            </span>
                          );
                        })()}
                      <SectionScoreBadge
                        score={computeFieldScore(
                          field.id,
                          dispositionFields?.required_fields,
                          dispositionFields?.missing_fields,
                        )}
                      />
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-3 pb-3">{nestedContent}</AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        </div>
      )}
    </div>
  );
};

// Interface for ScalarFieldInputElement props
interface ScalarFieldInputElementProps {
  field: FormField;
  value: any;
  error?: string;
  handleDataChange: (fieldPath: string, value: any) => void;
  onBlur?: (fieldPath: string) => void;
  /** Case ID for File-type fields, so uploads target the existing case. */
  caseRecordId?: string;
}

/**
 * Renders the appropriate input element for a scalar field based on its type.
 */
const ScalarFieldInputElement: React.FC<ScalarFieldInputElementProps> = ({
  field,
  value,
  error,
  handleDataChange,
  onBlur,
  caseRecordId,
}) => {
  const handleBlur = () => onBlur?.(field.id);
  switch (field.type) {
    case 'file':
      return (
        <FileField
          field={field}
          value={value}
          onChange={val => handleDataChange(field.id, val)}
          disabled={field.disabled}
          error={error}
          caseRecordId={caseRecordId}
        />
      );
    case 'dob':
      return (
        <DobField
          id={field.id}
          value={value}
          onChange={val => handleDataChange(field.id, val)}
          onBlur={handleBlur}
          error={error}
          placeholder={field.placeholder}
          disabled={field.disabled}
          allowUnknown={field.allowUnknown}
          compact
        />
      );
    case 'name-with-initials':
      return (
        <FullNameField
          id={field.id}
          value={value}
          onChange={val => handleDataChange(field.id, val)}
          error={error}
          placeholder={field.placeholder}
          disabled={field.disabled}
          compact
        />
      );
    case 'select':
      return field.options ? (
        <SearchableSelect
          id={field.id}
          value={value}
          onValueChange={val => {
            handleDataChange(field.id, val);
            handleBlur();
          }}
          options={field.options}
          placeholder={field.placeholder || 'Select...'}
          error={!!error}
          disabled={field.disabled}
          className="h-8 text-sm"
        />
      ) : (
        <Input
          type={field.type || 'text'}
          value={value}
          onChange={e => handleDataChange(field.id, e.target.value)}
          onBlur={handleBlur}
          placeholder={field.placeholder}
          disabled={field.disabled}
          className={`h-8 text-sm w-full ${error ? 'border-destructive focus-visible:ring-destructive' : ''}`}
        />
      );
    case 'date': {
      return (
        <DatePicker
          value={value}
          onChange={(date, unknown) => {
            handleDataChange(
              field.id,
              unknown ? UNKNOWN_DATE_VALUE : date ? format(date, 'yyyy-MM-dd') : '',
            );
            handleBlur();
          }}
          placeholder={field.placeholder || 'mm/dd/yyyy'}
          disabled={field.disabled}
          allowUnknown={field.allowUnknown}
          className={`h-8 ${error ? 'border-destructive focus-visible:ring-destructive' : ''}`}
        />
      );
    }
    case 'textarea':
      return (
        <textarea
          id={field.id}
          value={value ?? ''}
          onChange={e => handleDataChange(field.id, e.target.value)}
          onBlur={handleBlur}
          placeholder={field.placeholder}
          disabled={field.disabled}
          className={`w-full min-h-[100px] p-3 rounded-md border text-sm focus:outline-hidden focus:ring-2 focus:ring-offset-0 ${
            error ? 'border-destructive focus:ring-destructive' : ''
          }`}
        />
      );
    case 'phone':
      return (
        <ReactPhoneInput
          inputProps={{
            id: field.id,
            className: error ? 'error' : '',
            onBlur: handleBlur,
          }}
          country="us"
          value={value ?? ''}
          onChange={val => handleDataChange(field.id, val)}
          disabled={field.disabled}
          inputClass="w-full!"
          containerClass="phone-input-container"
          inputStyle={{
            width: '100%',
            height: '32px',
            fontSize: '14px',
            paddingLeft: '48px',
            borderRadius: '6px',
            border: error ? '1px solid #ef4444' : '1px solid hsl(var(--border))',
            backgroundColor: 'transparent',
            outline: 'none',
            transition: 'all 0.2s',
          }}
          buttonStyle={{
            borderRadius: '6px 0 0 6px',
            border: error ? '1px solid #ef4444' : '1px solid hsl(var(--border))',
            borderRight: 'none',
            backgroundColor: 'transparent',
            transition: 'all 0.2s',
          }}
          placeholder={field.placeholder}
          enableSearch
          searchPlaceholder="Search countries..."
          dropdownStyle={{
            zIndex: 99999,
            position: 'absolute',
          }}
        />
      );
    case 'number':
    case 'float':
      return (
        <Input
          type="number"
          {...(field.type === 'float' ? { step: 'any' } : {})}
          value={value ?? ''}
          onChange={e => handleDataChange(field.id, e.target.value ? Number(e.target.value) : '')}
          onBlur={handleBlur}
          placeholder={field.placeholder}
          disabled={field.disabled}
          className={`h-8 text-sm w-full ${error ? 'border-destructive focus-visible:ring-destructive' : ''}`}
        />
      );
    case 'multi_select': {
      const selectedVals = Array.isArray(value)
        ? value
        : typeof value === 'string' && value
          ? [value]
          : [];
      const definedOpts = field.options
        ? field.options.map(opt => ({ label: opt.label, value: String(opt.value) }))
        : [];
      // Legacy fallback: if a selected value doesn't match any defined option
      // (e.g. old free-text value before the field was changed to multi_select),
      // inject it as an ad-hoc option so it still appears checked in the dropdown.
      const definedVals = new Set(definedOpts.map(o => o.value));
      const orphanOpts = selectedVals
        .filter(v => !definedVals.has(v))
        .map(v => ({ label: v, value: v }));
      const effectiveOpts = [...definedOpts, ...orphanOpts];
      return field.options || orphanOpts.length > 0 ? (
        <MultiSelect
          options={effectiveOpts}
          selected={selectedVals}
          onChange={val => {
            handleDataChange(field.id, val);
            handleBlur();
          }}
          placeholder={field.placeholder || 'Select options...'}
          disabled={field.disabled}
          className={`h-8 text-sm ${error ? 'border-destructive' : ''}`}
          searchable
        />
      ) : (
        <Input
          type="text"
          value={value}
          onChange={e => handleDataChange(field.id, e.target.value)}
          onBlur={handleBlur}
          placeholder={field.placeholder}
          disabled={field.disabled}
          className={`h-8 text-sm w-full ${error ? 'border-destructive focus-visible:ring-destructive' : ''}`}
        />
      );
    }
    default:
      return (
        <Input
          type={field.type || 'text'}
          value={value}
          onChange={e => handleDataChange(field.id, e.target.value)}
          onBlur={handleBlur}
          placeholder={field.placeholder}
          disabled={field.disabled}
          className={`h-8 text-sm w-full ${error ? 'border-destructive focus-visible:ring-destructive' : ''}`}
        />
      );
  }
};

// Interface for AutoCalculatedPill props
interface AutoCalculatedPillProps {
  field: FormField;
  value: any;
  inline?: boolean;
}

/**
 * Renders the auto-calculated value pill for fields with `autoCalculates` set.
 * Use `inline` when displayed as a flex sibling rather than below the input.
 */
const AutoCalculatedPill: React.FC<AutoCalculatedPillProps> = ({ field, value, inline }) => {
  const result = computeAutoCalculatedValue(field, value);
  if (!result) return null;
  return (
    <div
      className={`flex items-center gap-1.5 text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-md ${inline ? 'shrink-0' : ''}`}
    >
      <span className="font-medium">{result.label}:</span>
      <span>{result.value}</span>
    </div>
  );
};

// Interface for ScalarFieldEditable props
interface ScalarFieldEditableProps {
  field: FormField;
  value: any;
  error?: string;
  fieldClassName: {
    container?: string;
    label?: string;
    value?: string;
    labelInline?: boolean;
  };
  handleDataChange: (fieldPath: string, value: any) => void;
  onFieldBlur?: (fieldPath: string) => void;
  /** Consolidated per-field suggestion props from useCaseInputStorage */
  getFieldSuggestionProps?: GetFieldSuggestionProps;
  /** Case ID for File-type fields, so uploads target the existing case. */
  caseRecordId?: string;
  /** Present when this field was marked "heard" during a call — renders a seek icon while editing. */
  outputHeardSeek?: HeardSeek;
  onNavigateToCall?: (callId: string, evidenceTimeStart?: string, evidenceTimeEnd?: string) => void;
  /** AI-extracted value for this field — rendered as a suggestion chip while editing (BV-1784). */
  extractedValue?: any;
  /** Extraction evidence (reasoning + cited transcript span) behind `extractedValue`. */
  extractedEvidence?: FieldEvidence[];
  /** Call the extraction came from — the chip's "Review" target. */
  extractedSourceCallId?: string;
  /** Evidence behind the field's current (persisted) value — reasoning shown next to the label. */
  outputEvidence?: FieldEvidence[];
  onNavigateToAudit?: (auditId: string) => void;
  /** When set, suggestions are limited to extractions from the call being viewed. */
  currentCallId?: string;
}

/**
 * Renders an editable scalar field (text, select, date, etc.)
 */
export const ScalarFieldEditable: React.FC<ScalarFieldEditableProps> = ({
  field,
  value,
  error,
  fieldClassName,
  handleDataChange,
  onFieldBlur,
  getFieldSuggestionProps,
  caseRecordId,
  outputHeardSeek,
  onNavigateToCall,
  extractedValue,
  extractedEvidence,
  extractedSourceCallId,
  outputEvidence,
  onNavigateToAudit,
  currentCallId,
}) => {
  // Get suggestion props for this field (if available)
  const sp = getFieldSuggestionProps?.(field.id, field.type);
  const hasSuggestionOrHint = sp?.suggestion !== undefined || sp?.hint !== undefined;

  // AI extraction on output fields (Human Audit / Summary). The read-only
  // EditableField renders it as a sparkle row with a Review button + reasoning
  // lightbulb; flipping the board into edit mode used to drop both (BV-1784).
  // Feed the extraction through the same chip the case-input form uses so the
  // suggestion, its provenance, and its chain of thought survive editing.
  //
  // Only when the caller supplies no `getFieldSuggestionProps` — a caller that
  // does (case-input form, the Review tab's human-call output board) owns
  // suggestion state for the field, including per-field dismissals, and must not
  // have a dismissed suggestion resurrected from extracted_data here.
  const [isSuggestionHidden, setIsSuggestionHidden] = useState(false);
  const showExtractedSuggestion =
    !sp &&
    !isSuggestionHidden &&
    shouldShowExtractedValue({
      extractedValue,
      value,
      field,
      currentCallId,
      sourceCallId: extractedSourceCallId,
    });

  // One shape for both suggestion sources so the render path below stays single.
  const suggestionProps =
    hasSuggestionOrHint && sp
      ? {
          suggestion: sp.suggestion,
          hint: sp.hint,
          isCollapsed: sp.isCollapsed,
          onAccept: () => {
            handleDataChange(field.id, sp.suggestion);
            sp.onAccept();
          },
          onReject: sp.onReject,
          onExpand: sp.onExpand,
          evidence: sp.evidence,
          sourceCallId: sp.sourceCallId,
          onNavigateToCall: sp.onNavigateToCall,
          onNavigateToAudit: sp.onNavigateToAudit,
        }
      : showExtractedSuggestion
        ? {
            suggestion: extractedValue,
            hint: undefined,
            isCollapsed: false,
            // Accepting stages the value in the open edit session (Save persists it),
            // then fires the blur/commit signal so disposition re-evaluates.
            onAccept: () => {
              handleDataChange(field.id, extractedValue);
              onFieldBlur?.(field.id);
            },
            onReject: () => setIsSuggestionHidden(true),
            onExpand: undefined,
            evidence: extractedEvidence,
            sourceCallId: extractedSourceCallId,
            onNavigateToCall,
            onNavigateToAudit,
          }
        : null;

  const inputElement = (
    <ScalarFieldInputElement
      field={field}
      value={value}
      error={error}
      handleDataChange={handleDataChange}
      onBlur={onFieldBlur}
      caseRecordId={caseRecordId}
    />
  );

  return (
    <div
      className={
        fieldClassName.container || 'group px-2 py-2 -mx-2 rounded hover:bg-muted transition-colors'
      }
    >
      <div
        className={
          fieldClassName.labelInline
            ? field.autoCalculates
              ? 'flex items-center gap-2'
              : 'flex justify-between items-center'
            : ''
        }
      >
        <div className={fieldClassName.labelInline ? '' : 'mb-1'}>
          <span className="inline-flex items-center gap-1.5">
            <label
              className={`${fieldClassName.label || 'text-xs text-muted-foreground'} ${error ? 'text-destructive' : ''}`}
            >
              {field.label}
              {field.validation?.required && <span className="text-destructive ml-1">*</span>}
            </label>
            {outputHeardSeek?.source_id && onNavigateToCall && (
              <HeardSeekButton heardSeek={outputHeardSeek} onNavigateToCall={onNavigateToCall} />
            )}
            {/* Provenance of the value already on the field — kept while editing so the
                auditor can still read the AI's reasoning for what it's about to change. */}
            {!!outputEvidence?.length && onNavigateToCall && (
              <>
                <ReviewButton
                  sourceCallId={extractedSourceCallId}
                  evidence={outputEvidence}
                  onNavigateToCall={onNavigateToCall}
                  onNavigateToAudit={onNavigateToAudit}
                  className="text-xs"
                />
                <ReasoningTooltip evidence={outputEvidence} />
              </>
            )}
          </span>
        </div>
        <div
          className={
            fieldClassName.labelInline
              ? field.autoCalculates
                ? `${fieldClassName.value || 'text-sm font-medium'} flex-1`
                : fieldClassName.value || 'text-sm font-medium'
              : 'text-sm font-medium'
          }
        >
          {suggestionProps ? (
            <InputWithSuggestion
              suggestion={suggestionProps.suggestion}
              hint={suggestionProps.hint}
              field={field}
              value={value}
              isCollapsed={suggestionProps.isCollapsed}
              isTextarea={field.type === 'textarea'}
              onAccept={suggestionProps.onAccept}
              onReject={suggestionProps.onReject}
              onExpand={suggestionProps.onExpand}
              evidence={suggestionProps.evidence}
              sourceCallId={suggestionProps.sourceCallId}
              onNavigateToCall={suggestionProps.onNavigateToCall}
              onNavigateToAudit={suggestionProps.onNavigateToAudit}
            >
              {inputElement}
            </InputWithSuggestion>
          ) : (
            inputElement
          )}
          {error && (
            <div className="text-destructive flex" style={{ fontSize: 'smaller' }}>
              {error}
            </div>
          )}
          {/* Show auto-calculated pill below input when NOT inline */}
          {!fieldClassName.labelInline && field.autoCalculates && (
            <div className="flex justify-end mt-1">
              <AutoCalculatedPill field={field} value={value} />
            </div>
          )}
        </div>
        {/* Show auto-calculated pill as sibling when inline */}
        {fieldClassName.labelInline && field.autoCalculates && (
          <AutoCalculatedPill field={field} value={value} inline />
        )}
      </div>
    </div>
  );
};

export const FieldGroupAccordion: React.FC<FieldGroupAccordionProps> = ({
  groups,
  data,
  extractedData,
  editableState = null,
  emptyStateMessage = {
    title: 'No Data Available',
    subtitle: 'Data will appear here once available',
  },
  className = {},
  fieldClassName = {},
  isEditable = false,
  section,
  onDataChange,
  onFieldBlur,
  showCollapseAll = true,
  defaultExpanded = true,
  externalExpandedItems,
  onExpandedItemsChange,
  canEditSummaries = false,
  editingSummaries = new Set(),
  onSummaryEdit,
  onSummarySave,
  onSummaryCancel,
  isLoadingSummary = false,
  onGroupEditClick,
  fieldError,
  isCaseReview = false,
  getFieldSuggestionProps,
  workflowType,
  dispositionFields,
  isHumanAudit,
  onAcceptExtracted,
  extractedMetadata,
  outputMetadata,
  onNavigateToCall,
  onNavigateToAudit,
  currentCallId,
  caseRecordId,
}) => {
  // Apply schema-based required fields to groups based on current data
  const groupsWithRequired = useMemo(() => {
    return applyRequiredFieldsToGroups(groups, data);
  }, [groups, data]);

  // Separate metadata group from other groups
  const { metadataGroup, otherGroups: unsortedGroups } = separateMetadataGroup(groupsWithRequired);

  // Sort groups to show "Extracted Fax Data" first
  const otherGroups = useMemo(() => {
    return [...unsortedGroups].sort((a, b) => {
      if (a.label === 'Extracted Fax Data') return -1;
      if (b.label === 'Extracted Fax Data') return 1;
      return 0;
    });
  }, [unsortedGroups]);

  // Compute per-section disposition completeness scores
  const sectionScores = useMemo(
    () =>
      computeSectionScores(
        otherGroups,
        dispositionFields?.required_fields,
        dispositionFields?.missing_fields,
      ),
    [otherGroups, dispositionFields?.required_fields, dispositionFields?.missing_fields],
  );

  // Build per-field disposition status map
  const dispositionMap = useMemo(
    () =>
      buildFieldDispositionMap(
        dispositionFields?.required_fields,
        dispositionFields?.missing_fields,
      ),
    [dispositionFields?.required_fields, dispositionFields?.missing_fields],
  );

  // State for controlling which accordion items are expanded
  const [expandedItems, setExpandedItems] = useState<string[]>(() => {
    if (externalExpandedItems !== undefined) {
      return externalExpandedItems;
    }
    return defaultExpanded ? createAllAccordionIndices(otherGroups.length) : [];
  });

  // State for tracking which summaries are expanded (show full text)
  const [expandedSummaries, setExpandedSummaries] = useState<Set<string>>(new Set());

  // State for tracking which summaries are truncated (need "Show more" button)
  const [truncatedSummaries, setTruncatedSummaries] = useState<Set<string>>(new Set());

  // Update expanded items when external control changes
  useEffect(() => {
    if (externalExpandedItems !== undefined) {
      setExpandedItems(externalExpandedItems);
    }
  }, [externalExpandedItems]);

  const handleExpandedItemsChange = (items: string[]) => {
    setExpandedItems(items);
    onExpandedItemsChange?.(items);
  };

  const allItemsExpanded = areAllAccordionItemsExpanded(expandedItems, otherGroups.length);

  const handleToggleAll = () => {
    handleExpandedItemsChange(toggleAllAccordionItems(expandedItems, otherGroups.length));
  };

  // Toggle summary expansion
  const toggleSummaryExpansion = (groupLabel: string) => {
    setExpandedSummaries(prev => {
      const newSet = new Set(prev);
      if (newSet.has(groupLabel)) {
        newSet.delete(groupLabel);
      } else {
        newSet.add(groupLabel);
      }
      return newSet;
    });
  };

  // Handle truncation detection
  const handleTruncationDetected = (groupLabel: string, isTruncated: boolean) => {
    setTruncatedSummaries(prev => {
      const newSet = new Set(prev);
      if (isTruncated) {
        newSet.add(groupLabel);
      } else {
        newSet.delete(groupLabel);
      }
      return newSet;
    });
  };

  // Handle metadata field change
  const handleMetadataChange = (fieldId: string, value: string) => {
    if (onDataChange) {
      const updatedData = { ...data, [fieldId]: value };
      onDataChange(updatedData, fieldId);
    }
  };

  if (groups.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <div className="text-muted-foreground mb-4">
          <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
        </div>
        <p className="text-muted-foreground text-sm font-medium mb-1">{emptyStateMessage.title}</p>
        <p className="text-muted-foreground text-xs">{emptyStateMessage.subtitle}</p>
      </div>
    );
  }

  // Default classes
  const defaultClasses = {
    accordion: 'space-y-3',
    accordionContent: 'px-3 pb-3',
    fieldContainer: '',
    fieldWrapper: '',
    fieldSeparator: '',
  };

  // Merge default classes with provided classes
  const classes = {
    accordion: className.accordion || defaultClasses.accordion,
    accordionContent: className.accordionContent || defaultClasses.accordionContent,
    fieldContainer: className.fieldContainer || defaultClasses.fieldContainer,
    fieldWrapper: className.fieldWrapper || defaultClasses.fieldWrapper,
    fieldSeparator: className.fieldSeparator || defaultClasses.fieldSeparator,
  };

  // Handle data change for accordion-wide editing (dot paths, e.g.
  // `drug.0.prior_auth_process.prior_authorization_fax_number`).
  //
  // Goes through `setNestedValue` rather than walking the path by hand (LA-7951). The
  // hand-rolled walk shallow-copied only the top level and then wrote into the nested
  // objects IN PLACE, and it descended into whatever it found — so a section already
  // wiped to `[]` took each edit as a named property on the array, which JSON drops:
  // Edit → Save into that section silently saved nothing. `setNestedValue` clones every
  // container on the path and repairs a `[]` where the path names a property.
  const handleDataChange = (fieldPath: string, value: any) => {
    if (onDataChange) {
      onDataChange(setNestedValue(data ?? {}, fieldPath, value), fieldPath);
    }
  };

  return (
    <div>
      {/* Metadata section - rendered as card buttons at top */}
      {metadataGroup && metadataGroup.fields.filter(f => f.type === 'radio').length > 0 && (
        <RadioCardGroup
          group={metadataGroup}
          formData={data}
          fieldErrors={fieldError || {}}
          readOnly={!isEditable}
          onFieldChange={handleMetadataChange}
          variant="inline"
        />
      )}

      {showCollapseAll && otherGroups.length > 0 && (
        <div className="flex justify-end mb-3">
          <Button
            variant="outline"
            size="sm"
            onClick={handleToggleAll}
            className="h-5 px-2 text-xs"
          >
            <ChevronsDownUp className="w-3 h-3 mr-1" />
            {allItemsExpanded ? 'Collapse All' : 'Expand All'}
          </Button>
        </div>
      )}
      <Accordion
        type="multiple"
        className={classes.accordion}
        value={expandedItems}
        onValueChange={handleExpandedItemsChange}
      >
        {otherGroups.map((group, groupIndex) => {
          // Skipped in place rather than filtered out, so groupIndex (the accordion value
          // and expanded-state key) stays aligned with `otherGroups`.
          if (hasOnlyHiddenFields(group)) return null;
          return (
            <AccordionItem
              key={groupIndex}
              value={groupIndex.toString()}
              data-accordion-group-index={groupIndex}
              className="bg-card border rounded-lg"
            >
              <AccordionTrigger
                className="px-3 py-3 text-sm font-semibold text-foreground hover:bg-muted rounded-lg data-[state=open]:rounded-b-none gap-2"
                action={
                  <div className="flex items-start gap-2">
                    {canEditSummaries && !editingSummaries.has(group.label) && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={e => {
                          e.stopPropagation();
                          onSummaryEdit?.(group.label);
                        }}
                        className="h-6 px-2 text-muted-foreground hover:text-foreground"
                        title={group.summary ? 'Edit summary' : 'Add summary'}
                      >
                        <Edit3 className="w-3 h-3" />
                      </Button>
                    )}
                    {onGroupEditClick && !isCaseReview && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={e => {
                          e.stopPropagation();
                          onGroupEditClick(groupIndex);
                        }}
                        className="h-5 px-2 text-xs"
                      >
                        <Edit3 className="w-3 h-3 mr-1" />
                        Edit
                      </Button>
                    )}
                  </div>
                }
              >
                <div className="flex-1 text-left">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span>{group.label}</span>
                      <SectionScoreBadge score={sectionScores.get(group.label)} />
                    </div>
                    {editingSummaries.has(group.label) ? (
                      // Show EditableSummary in header when editing
                      <div className="mt-2" onClick={e => e.stopPropagation()}>
                        <EditableSummary
                          value={group.summary || ''}
                          onSave={newValue => onSummarySave?.(group.label, newValue)}
                          onCancel={() => onSummaryCancel?.(group.label)}
                          placeholder="Enter summary for this section..."
                          isLoading={isLoadingSummary}
                        />
                      </div>
                    ) : group.summary ? (
                      // Show summary text with expand/collapse functionality
                      <div onClick={e => e.stopPropagation()}>
                        <SummaryDisplay
                          summary={group.summary}
                          isExpanded={expandedSummaries.has(group.label)}
                          onTruncationDetected={isTruncated =>
                            handleTruncationDetected(group.label, isTruncated)
                          }
                        />
                        {truncatedSummaries.has(group.label) && (
                          // Span + role=button instead of <button> — the parent
                          // AccordionTrigger renders as a <button>, and nested
                          // <button>s violate DOM nesting rules.
                          <span
                            role="button"
                            tabIndex={0}
                            aria-expanded={expandedSummaries.has(group.label)}
                            onClick={e => {
                              e.stopPropagation();
                              toggleSummaryExpansion(group.label);
                            }}
                            onKeyDown={e => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                e.stopPropagation();
                                toggleSummaryExpansion(group.label);
                              }
                            }}
                            className="text-xs text-muted-foreground hover:text-foreground transition-colors mt-1 flex items-center gap-1 cursor-pointer"
                          >
                            {expandedSummaries.has(group.label) ? (
                              <>
                                Show less <ChevronUp className="w-3 h-3" />
                              </>
                            ) : (
                              <>
                                Show more <ChevronDown className="w-3 h-3" />
                              </>
                            )}
                          </span>
                        )}
                      </div>
                    ) : (
                      // Show placeholder when no summary and editing is enabled
                      // Only for medbv workflows
                      canEditSummaries &&
                      workflowType === 'medbv' && (
                        <div className="text-xs font-normal text-muted-foreground mt-1">
                          <span className="italic">No summary yet</span>
                        </div>
                      )
                    )}
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent className={classes.accordionContent}>
                <GroupFieldsRenderer
                  fields={group.fields}
                  groupLabel={group.label}
                  data={data}
                  extractedData={extractedData}
                  isEditable={group.label === 'Extracted Fax Data' ? false : isEditable}
                  section={section}
                  workflowType={workflowType}
                  fieldError={fieldError}
                  fieldClassName={fieldClassName}
                  editableState={editableState}
                  classes={classes}
                  handleDataChange={handleDataChange}
                  onFieldBlur={onFieldBlur}
                  getFieldSuggestionProps={getFieldSuggestionProps}
                  dispositionMap={dispositionMap.size > 0 ? dispositionMap : undefined}
                  dispositionFields={dispositionFields}
                  isHumanAudit={isHumanAudit}
                  onAcceptExtracted={onAcceptExtracted}
                  extractedMetadata={extractedMetadata}
                  outputMetadata={outputMetadata}
                  onNavigateToCall={onNavigateToCall}
                  onNavigateToAudit={onNavigateToAudit}
                  currentCallId={currentCallId}
                  caseRecordId={caseRecordId}
                />
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>
    </div>
  );
};
